import React from "react";
import "./SessionAnalysis.css";
import { useSelector } from "react-redux";
import {
    selectTimerStatus,
    selectPlannedLuckets,
    selectActiveLuckets,
    selectLucketTimeSegments
} from "../../sessionWatchSlice";
import { TIMER_STATUS } from "../../constants";

export const SessionAnalysis = ({ compact = false, }) => {
    const timerStatus = useSelector(selectTimerStatus);
    const plannedLuckets = useSelector(selectPlannedLuckets);
    const activeLuckets = useSelector(selectActiveLuckets);
    const lucketTimeSegments = useSelector(selectLucketTimeSegments);
    const allBuckets = useSelector((state) => state.bucketList.buckets);

    // Calculate time per lucket
    const calculateLucketTime = () => {
        const timeByLucket = {};

        lucketTimeSegments.forEach(segment => {
            const duration = segment.endTime
                ? (segment.endTime - segment.startTime) / 1000  // seconds
                : (new Date().getTime() - segment.startTime) / 1000;

            if (timeByLucket[segment.lucketRowID]) {
                timeByLucket[segment.lucketRowID] += duration;
            } else {
                timeByLucket[segment.lucketRowID] = duration;
            }
        });

        return timeByLucket;
    };

    const lucketTimeData = calculateLucketTime();

    // Get lucket names
    const getLucketName = (rowID) => {
        const lucket = allBuckets?.find(b => b.rowID === rowID);
        return lucket?.name || 'Unknown';
    };

    // Show session analysis if session is active or just ended
    const showSessionAnalysis = timerStatus === TIMER_STATUS[1] ||
        (plannedLuckets.length > 0 || activeLuckets.length > 0);

    if (!showSessionAnalysis) return null;

    // Compact mode: Display as table
    if (compact) {
        // Combine all lockets data
        const allLucketsData = [];
        const allLucketIDs = new Set([...plannedLuckets, ...activeLuckets]);

        allLucketIDs.forEach(rowID => {
            allLucketsData.push({
                rowID,
                name: getLucketName(rowID),
                planned: plannedLuckets.includes(rowID),
                worked: activeLuckets.includes(rowID),
                time: lucketTimeData[rowID] || 0
            });
        });

        return (
            <div className="session-analysis compact">
                <h3>📊 Session</h3>
                <table className="session-analysis-table">
                    <thead>
                        <tr>
                            <th>Lucket</th>
                            <th>📋</th>
                            <th>✅</th>
                            <th>⏱ Time</th>
                        </tr>
                    </thead>
                    <tbody>
                        {allLucketsData.length > 0 ? (
                            allLucketsData.map(lucket => (
                                <tr key={lucket.rowID}>
                                    <td>{lucket.name}</td>
                                    <td className="center">{lucket.planned ? '✓' : ''}</td>
                                    <td className="center">{lucket.worked ? '✓' : ''}</td>
                                    <td className="right">{lucket.time > 0 ? `${Math.round(lucket.time)}s` : '-'}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" className="center empty">No data</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        );
    }

    // Regular mode: Display as columns
    return (
        <div className="session-analysis">
            <h3>{timerStatus === TIMER_STATUS[0] ? "Session Outlook" : 'Session Analysis'}</h3>

            {/* Planned vs Actual */}
            <div className="planned-vs-actual">
                <div className="analysis-column">
                    <h4>📋 Planned:</h4>
                    <ul>
                        {plannedLuckets.map(rowID => (
                            <li key={rowID}>
                                {getLucketName(rowID)}
                                {activeLuckets.includes(rowID) && <span className="worked-badge">✓</span>}
                            </li>
                        ))}
                        {plannedLuckets.length === 0 && <li className="empty">None</li>}
                    </ul>
                </div>

                <div className="analysis-column">
                    <h4>✅ Worked On:</h4>
                    <ul>
                        {activeLuckets.map(rowID => (
                            <li key={rowID}>
                                {getLucketName(rowID)}
                                {!plannedLuckets.includes(rowID) && <span className="unplanned-badge">+</span>}
                                {lucketTimeData[rowID] && (
                                    <span className="time-badge">
                                        {Math.round(lucketTimeData[rowID])}s
                                    </span>
                                )}
                            </li>
                        ))}
                        {activeLuckets.length === 0 && <li className="empty">None</li>}
                    </ul>
                </div>
            </div>

            {/* Time Breakdown */}
            {Object.keys(lucketTimeData).length > 0 && (
                <div className="time-breakdown">
                    <h4>⏱ Time Distribution:</h4>
                    {Object.entries(lucketTimeData)
                        .sort((a, b) => b[1] - a[1])
                        .map(([rowID, seconds]) => (
                            <div key={rowID} className="time-bar-container">
                                <span className="lucket-name-bar">{getLucketName(rowID)}</span>
                                <div className="time-bar-wrapper">
                                    <div
                                        className="time-bar"
                                        style={{
                                            width: `${(seconds / Math.max(...Object.values(lucketTimeData))) * 100}%`
                                        }}
                                    />
                                </div>
                                <span className="time-value">{Math.round(seconds)}s</span>
                            </div>
                        ))
                    }
                </div>
            )}
        </div>
    );
};
